// types to use for ag-grid colDefs 'type' property

import {
  //FormatDateHelper,
  //FormatDateTimeLocal,
  DurationSecondsToHumanized,
  FormatTimeHelper,
} from '../../js/filters-helpers';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import { DateTime } from 'luxon';

const toJSDate = (isoDateTime) => {
  // ag-grid date filters want JS Date objects
  if (!isoDateTime) {
    return null;
  }
  const luxonDateTime = DateTime.fromISO(isoDateTime);
  return luxonDateTime.isValid ? luxonDateTime.toJSDate() : null;
};
const formatDate = (jsDate) => {
  if (!jsDate) {
    return '';
  }
  const luxonDateTime = DateTime.fromJSDate(jsDate);
  return luxonDateTime.isValid ? luxonDateTime.toLocaleString(DateTime.DATE_SHORT) : '';
};
const formatDateTime = (jsDate) => {
  if (!jsDate) {
    return '';
  }
  const luxonDateTime = DateTime.fromJSDate(jsDate);
  return luxonDateTime.isValid ? luxonDateTime.toLocaleString(DateTime.DATETIME_SHORT) : '';
};
const formatRfxListItem = (rfx) => {
  if (!rfx || typeof rfx !== 'object') {
    return '';
  }
  const label = rfx.rfx_type || '';
  const title = rfx.title || '';
  if (label && title) {
    return `${label}: ${title}`;
  }
  return title || label || '';
};
const formatRfxListValue = (value) => {
  if (!Array.isArray(value)) {
    return value ? String(value) : '';
  }
  return value.map((rfx) => formatRfxListItem(rfx)).filter(Boolean).join(', ');
};

// FILTERS / FILTER OPTIONS - customizing what 'operators' are possible for each filter type
// https://www.ag-grid.com/archive/25.3.0/vue-grid/filter-provided-simple/#simple-filter-options
// these are the defaults from ag-grid... you can add/remove as needed -- include objects that meet interface IFilterOptionDef
// you can also add 'empty' to show a 'Choose one' option at the top of the dropdown
const standardTextFilterOptions = ['contains', 'notContains', 'equals', 'notEqual', 'startsWith', 'endsWith'];
const isBlankTextFilterOption = {
  // backend will look for this key (isBlank) and know how to handle it based on the field (native Django field or JSON field) - but must still enter 'test' for local filtering
  displayKey: 'isBlank',
  displayName: 'Is Blank',
  test: function (filterValue, cellValue) {
    // return true if the filter is a match
    return [null, '', undefined].includes(cellValue);
  },
  hideFilterInput: true,
};
const notBlankTextFilterOption = {
  // backend will look for this key (isBlank) and know how to handle it based on the field (native Django field or JSON field) - but must still enter 'test' for local filtering
  displayKey: 'notBlank',
  displayName: 'Not Blank',
  test: function (filterValue, cellValue) {
    return !!cellValue;
  },
  hideFilterInput: true,
};
const customTextFilterOptions = [...standardTextFilterOptions, isBlankTextFilterOption, notBlankTextFilterOption];
// TODO - add more of these for different col types (dates, lookups, etc.) - must make sure backend knows how to handle them

export default {
  linkCell: {
    // add this type to whatever the first/main cell is to link to the detail page
    cellRenderer: (params) => {
      // wrap it in a link
      //console.log('params in cellRenderer', params);
      // if there is no valueFormatter, then valueFormatted will be null (kinda dumb)
      // use internal_detail_url / external_detail_url ??? or permalink_url or full_permalink_url ???
      // permalink_url should be more generic way of doing it
      // March 27 2025 - we changed to use more generic URL property, can make it dynamic in GridBuilder backend - '_detail_url'
      // if (params.data?.permalink_url) {
      //     return `<a href="${params.data.permalink_url}" class="font-weight-600">${params.valueFormatted || params.value}</a>`
      if (params.data?._detail_url) {
        return `<a href="${params.data._detail_url}" class="font-weight-600">${params.valueFormatted || params.value}</a>`;
      } else {
        return params.valueFormatted || params.value;
      }
    },
    pinned: 'left', // pin to left side for easier access to link in wide grids
    lockPinned: true, // prevent unpinning
    // Can't figure out how to turn off context menu for only one column... so we'll disable for entire grid for now
    /* onCellContextMenu: (params) => {
            // cancel the event so context menu does not show
            console.log('params from onCellContextMenu', params);
            const evt = params.event;
            if (evt && evt.cancelable) {
                console.log('should now preventDefault');
                evt.preventDefault(); // DOES NOT WORK
            }
        }, */
  },
  lookup: {
    // ACTUALLY DON'T NEED valueFormatter HERE -- IF YOU HAVE refData THEN GRID AUTOMATICALLY USES THAT IN valueFormatter
    // AND YOU CAN JUST USE THE VALUE GETTER FOR DEFAULT COLDEF
    // AND FOR EXCEL EXPORT, YOU CAN USE THE EXCEL CALLBACK TO FORMAT VALUES FROM refData
    // EXCEPT FOR SERVER-SIDE ROW MODEL -- NEED TO PROVIDE ALL OPTIONS IN ADVANCE
    enableRowGroup: true,
    enablePivot: true,
    // TODO: This should be used when we return objects {ID: {label:String, _url: URL}} instead of {ID: String} mapping
    // valueFormatter: (params) => {
    //   const value = params.value;
    //   const option = params.colDef.refData[value];
    //   return option ? option.label : value;
    // },
    // cellRenderer: (params) => {
    //   // params.value is the ID of the lookup option
    //   // params.colDef.refData is the mapping of ID to option object
    //   const value = params.value;
    //   const option = params.colDef.refData[value];
    //   if (option) {
    //     // if option has a URL, use it to link to the detail page
    //     if (option._url) {
    //       return `<a href="${option._url}" target="_blank">${option.label}</a>`;
    //     }
    //     // otherwise just show the label
    //     return option.label;
    //   }
    // },
    // do we need filter: 'agSetColumnFilter' here?  did not have it set.... still works
    filterParams: {
      // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
      // NOTE: May 2026, we were seeing HUGE problems with set filter when people would just click each checkbox, which reloads the entire grid... on big grids this crashed the server!
      // so we will use 'apply' on these, in addition to 'reset' (and maybe 'cancel') - order here is what appears in UI
      buttons: ['apply', 'reset', 'cancel'],
      closeOnApply: true, // if you have an 'apply' button, you probably want true here, otherwise false
      suppressSorting: true, // suppress built-in sorting (it will sort filter options by ID, which is not what we want... we will do sorting below)
      refreshValuesOnOpen: true,
      excelMode: 'mac', // there is difference between the default, Excel-windows and Excel-mac modes... excelMode: 'mac' seems to be the best IMHO
      applyMiniFilterWhileTyping: false, // excelMode 'mac' defaults this to true, which (with the 'apply' button + closeOnApply) applies the filter and closes the popup on every keystroke in the mini filter
      values: (params) => {
        // need to get values from refData on columnDef
        console.log('params in lookup filterParams.values', params);
        //const vals = Object.keys(params.colDef.refData);  // make sure you always have refData?  sort by values, alphabetically?
        const vals = sortBy(
          Object.entries(params.colDef.refData).map(([k, v]) => ({ key: k, value: v })),
          (o) => String(o.value).toLocaleLowerCase(),
        ).map((e) => e.key);
        params.success(vals); // that returns the values to the filter dropdown
      },
      // TODO: We should use this when we return objects {ID: {label:String, _url: URL}} instead of {ID: String} mapping
      //   values: (params) => {
      //     // need to get values from refData on columnDef
      //     console.log('params in lookup filterParams.values', params);
      //     const refData = params.colDef.refData;
      //     // Convert refData to an array of { key, label } objects
      //     const entries = Object.entries(refData).map(([key, value]) => ({
      //       key,
      //       label: value.label,
      //     }));
      //     // Sort entries alphabetically by label (case-insensitive)
      //     const sortedEntries = sortBy(entries, (entry) => String(entry.label).toLocaleLowerCase());
      //     // Extract just the keys for the filter dropdown
      //     const vals = sortedEntries.map((entry) => entry.key);
      //     params.success(vals); // that returns the values to the filter dropdown
      //   },
      //   valueFormatter: (params) => {
      //     console.log('params in lookup filterParams.valueFormatter', params);
      //     const value = params.value;
      //     const option = params.colDef.refData[value];
      //     return option ? option.label : value;
      //   },
    },
  },
  string: {
    enableRowGroup: true, // there will likely be strings fields that are not lookups, but still lookup-ish... so default to true
    filter: 'agTextColumnFilter',
    filterParams: {
      // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
      buttons: ['clear'],
      closeOnApply: true,
      filterOptions: [...customTextFilterOptions],
      suppressAndOrCondition: true, // don't show the AND/OR dropdown -- too complex on backend for now (particularly 'OR' with all lookups)
    },
  }, // add if/when needed
  integer: {
    cellClass: 'number-cell', // makes things right aligned
    enableRowGroup: true, // makes senses for integers certainly... maybe not floats, but ints OK
    filter: 'agNumberColumnFilter',
    filterParams: {
      // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
      buttons: ['clear'],
      closeOnApply: true,
      suppressAndOrCondition: true, // don't show the AND/OR dropdown -- too complex on backend for now (particularly 'OR' with all lookups)
    },
  }, // add if/when needed
  money: {
    cellClass: 'number-cell', // makes things right aligned
    filter: 'agNumberColumnFilter',
    filterParams: {
      // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
      buttons: ['clear'],
      closeOnApply: true,
      suppressAndOrCondition: true, // don't show the AND/OR dropdown -- too complex on backend for now (particularly 'OR' with all lookups)
    },
    valueGetter: (params) => {
      // might be null, or an object in many different formats, or djmoney DRF serializer field, which splits the amount/currency as separate fields (stupid)
      // for now, we're building this for cases where you'll either have null, or a complex object like this:  { _type: 'Money', value: { amount: '1.05', currency: 'USD'}}
      // if things come DIRECTLY from some JSONFields, you could possibly also have a _type: "Decimal" nested inside amount
      // for valueGetter, we want to be able to sort things, so we return just the NUMBER itself
      const val = get(params.data, params.colDef.field);
      if (!val) {
        return null;
      } else if (val['_type'] === 'Money') {
        const amount = val.value?.amount?.value ?? val.value?.amount; // go for the extra nested one first (Decimal nested in 'amount'), fall back to just having a string in 'amount'
        // that might be a STRING (likely is) - need to convert to Number - also conserve the correct decimal places!  maybe pass that in somwhere?
        // not needed... const currencyCode = val.value?.currency;
        return Number(amount);
      }
      return val; // otherwise just return whatever we got from the normal get() - likely undefined, which will be OK for the grid
    },
    valueFormatter: (params) => {
      // might as well just start over getting things?  or we could get amount from params.value, which we got in valueGetter
      // remember to handle things lik 0 correctly... falsey, but maybe valid amount / params.value
      const val = get(params.data, params.colDef.field);
      const defaultFormatted = null;

      if (!val) {
        return defaultFormatted;
      } else if (val['_type'] === 'Money') {
        const amount = val.value?.amount?.value ?? val.value?.amount; // go for the extra nested one first (Decimal nested in 'amount'), fall back to just having a string in 'amount'
        // that might be a STRING (likely is) - need to convert to Number - also conserve the correct decimal places!  maybe pass that in somwhere?
        const amountNumber = Number(amount);
        const currencyCode = val.value?.currency; // MUST have this
        if (!currencyCode || Number.isNaN(amountNumber)) {
          return defaultFormatted;
        }
        const theLocale = undefined; // 'en-US';  // very small-minded of me... check browser instead?  or just use undefined, seems to be the way per MDN
        const currencyFormat = {
          style: 'currency',
          currency: currencyCode,
        };
        return amountNumber.toLocaleString(theLocale, currencyFormat);
      }
      return defaultFormatted; // just the final default... maybe something else is better
    },
  }, // add if/when needed
  float: {
    cellClass: 'number-cell', // makes things right aligned
    filter: 'agNumberColumnFilter',
    filterParams: {
      // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
      buttons: ['clear'],
      closeOnApply: true,
      suppressAndOrCondition: true, // don't show the AND/OR dropdown -- too complex on backend for now (particularly 'OR' with all lookups)
    },
  }, // add if/when needed
  decimal: {
    cellClass: 'number-cell', // makes things right aligned
    filter: 'agNumberColumnFilter',
    filterParams: {
      // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
      buttons: ['clear'],
      closeOnApply: true,
      suppressAndOrCondition: true, // don't show the AND/OR dropdown -- too complex on backend for now (particularly 'OR' with all lookups)
    },
  }, // add if/when needed
  date: {
    cellClass: 'centered-cell',
    filter: 'agDateColumnFilter',
    filterParams: {
      // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
      buttons: ['clear', 'reset'],
      closeOnApply: true,
      suppressAndOrCondition: true, // don't show the AND/OR dropdown -- too complex on backend for now (particularly 'OR' with all lookups)
    },
    enableRowGroup: true, // makes sense for 'date' but maybe not 'datetime'
    valueGetter: (params) => {
      // params.colDef.field is our dotted path
      const rawValue = get(params.data, params.colDef.field); // use this instead of expression in case a row does not have that nested value.... lodash get has built-in optional chaining basically
      return toJSDate(rawValue);
    },
    valueFormatter: (params) => {
      return formatDate(params.value);
    },
  },
  time: {
    valueFormatter: (params) => {
      return FormatTimeHelper(params.value);
    },
  },
  datetime: {
    // added June 28 2025, experimental, with custom compact renderer with tooltip for more info
    cellClass: 'centered-cell',
    maxWidth: 150, // this uses the compact renderer, so we don't need much width
    filter: 'agDateColumnFilter',
    filterParams: {
      // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
      buttons: ['clear', 'reset'],
      closeOnApply: true,
      suppressAndOrCondition: true, // don't show the AND/OR dropdown -- too complex on backend for now (particularly 'OR' with all lookups)
    },
    valueGetter: (params) => {
      // params.colDef.field is our dotted path
      //console.log('params in datetime valueGetter', params);
      const rawValue = get(params.data, params.colDef.field); // use this instead of expression in case a row does not have that nested value.... lodash get has built-in optional chaining basically
      //console.log('rawValue', rawValue);
      return toJSDate(rawValue);  // we use JSDate because ag-grid date filters want JS Date objects
    },
    valueFormatter: (params) => {
      // we use a valueFormatter for showing dates in random other places (exports? etc.) - but our renderer will handle most of the work
      return formatDateTime(params.value);
    },
    cellRenderer: 'compactDateTimeRenderer', // use our custom renderer that shows a compact date/time with tooltip for more info
  },
  datetimeOld: {
    // REPLACED BY datetime above, but keeping for reference because new one may have issues
    //hide: true,
    cellClass: 'centered-cell',
    filter: 'agDateColumnFilter',
    filterParams: {
      // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
      buttons: ['clear', 'reset'],
      closeOnApply: true,
      suppressAndOrCondition: true, // don't show the AND/OR dropdown -- too complex on backend for now (particularly 'OR' with all lookups)
    },
    valueGetter: (params) => {
      // params.colDef.field is our dotted path
      //console.log('params in datetime valueGetter', params);
      const rawValue = get(params.data, params.colDef.field); // use this instead of expression in case a row does not have that nested value.... lodash get has built-in optional chaining basically
      //console.log('rawValue', rawValue);
      return toJSDate(rawValue);
    },
    valueFormatter: (params) => {
      return formatDateTime(params.value);
    },
  },
  seconds: {
    valueFormatter: (params) => {
      return DurationSecondsToHumanized(params.value);
    },
  },
  timedelta: {
    valueFormatter: (params) => {
      return DurationSecondsToHumanized(params.value);
    },
  }, // add if/when needed (what do you get from backend??? seconds? iso duration?)
  boolean: {
    cellClass: 'centered-cell',
    enableRowGroup: true,
    valueFormatter: (params) => {
      // gotta find a way to get access to the custom true/false labels that we pass in as cellRendererParams when building colDefs... maybe also use those in filter dropdown??  for now, just return 'true'/'false' strings to match what is used in filter dropdown, and then we can use the cellRendererParams to show whatever we want in the cellRenderer
      // we have access to the colDef and its cellRendererParams in the valueFormatter params.colDef, we get from there
      const colDefCellRendererParams = params.colDef.cellRendererParams || {};
      const booleanTrueLabel = colDefCellRendererParams.booleanTrueLabel || 'True';
      const booleanFalseLabel = colDefCellRendererParams.booleanFalseLabel || 'False';
      switch (params.value) {
        case true:
          return booleanTrueLabel;
        case 'true': // handle stringified version from filter dropdown
          return booleanTrueLabel;
        case false:
          return booleanFalseLabel;
        case 'false': // handle stringified version from filter dropdown
          return booleanFalseLabel;
        default:
          return ''; // keep all else as empty string - maybe also do this in valueGetter?
      }
    },
    cellRenderer: (params) => {
      // we add cellRendererParams with booleanTrueLabel and booleanFalseLabel when building colDefs
      switch (params.value) {  // in here, the params.value should be the actual boolean value (not stringified), because it comes from the valueGetter, which should return a real boolean... but just in case, we can handle stringified versions too
        case true:
          return params.booleanTrueLabel || 'True';
        case 'true': // handle stringified version from filter dropdown
          return params.booleanTrueLabel || 'True';
        case false:
          return params.booleanFalseLabel || 'False';
        case 'false': // handle stringified version from filter dropdown
          return params.booleanFalseLabel || 'False';
        default:
          return '';
      }
    },
    filterParams: {
      // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
      buttons: ['reset'],
      closeOnApply: false,
      suppressSorting: true, // suppress built-in sorting (it will sort filter options by ID, which is not what we want... we will do sorting below)
      //refreshValuesOnOpen: true,
      values: [true, false],
      // just let the default valueFormatter handle it
      valueFormatter: (params) => {
        console.log('params in boolean filterParams.valueFormatter', params);
        // NOTE: the values here may be stringified versions of true/false, because that's how they come from the filter dropdown... so we need to handle both boolean and string versions of true/false
        // gotta find a way to get access to the custom true/false labels that we pass in as cellRendererParams when building colDefs... maybe also use those in filter dropdown??  for now, just return 'true'/'false' strings to match what is used in filter dropdown, and then we can use the cellRendererParams to show whatever we want in the cellRenderer
        // we have access to the colDef and its cellRendererParams in the valueFormatter params.colDef, we get from there
        const colDefCellRendererParams = params.colDef.cellRendererParams || {};
        const booleanTrueLabel = colDefCellRendererParams.booleanTrueLabel || 'True';
        const booleanFalseLabel = colDefCellRendererParams.booleanFalseLabel || 'False';
        return [true, 'true'].includes(params.value) ? booleanTrueLabel : [false, 'false'].includes(params.value) ? booleanFalseLabel : params.value;
      },
      // values: params => { // need to get values from refData on columnDef
      //     console.log('params in lookup filterParams.values', params);
      //     //const vals = Object.keys(params.colDef.refData);  // make sure you always have refData?  sort by values, alphabetically?
      //     const vals = sortBy(Object.entries(params.colDef.refData).map(([k, v]) => ({key: k, value: v})), (o) => String(o.value).toLocaleLowerCase()).map(e => e.key);
      //     params.success(vals);  // that returns the values to the filter dropdown
      // },
    },
  }, // add if/when needed (so you know how to specify what true/false actually mean)
  unknown: {}, // if backend doesn't know what type it is, this is the default
  list: {
    //enableRowGroup: true,  // does not work out of the box at least
  }, // list of names, etc.
  tu_list: {
    // list of TUs -- values will be a list of TU IDs -- we get names from context! must have all TUs in context for this to work
    // the backend sends options_from_context for these cols, so refData is the grid context's { id: full_name } mapping (see makeAgGridColDef)
    // for filter/sortable/enableRowGroup -- let backend override these to false
    enablePivot: false,
    filter: false, // add set filter config stuff later if you allow this here
    valueFormatter: (params) => {
      const tuRefData = params.colDef.refData || params.context.tenant_users || {}; // should be mapping of { id: full_name }
      return params.value.map((id) => tuRefData[id] || id).join(', ');
    },
    // wrap text?  use special renderer?  this could get long
  },
  prefetched_overdue_nodes: {
    // SPECIAL colDef -- shows one (OR MORE) overdue nodes
    // add some endpoint to lazy-load the details?  like assignees, etc.
    cellRenderer: 'prefetchedOverdueNodesRenderer', // the params.value is the OverdueNodeSchema from backend
    autoHeight: true, // so it can display our custom renderer properly
  },
  prefetched_active_nodes: {
    // SPECIAL colDef -- shows one (OR MORE) active nodes
    cellRenderer: 'prefetchedActiveNodesRenderer', // the params.value is the ActiveNodeSchema from backend
    autoHeight: true, // so it can display our custom renderer properly
  },
  phone_numbers: {
    // Render BootstrapPhoneNumberInput for each phone number (read-only)
    // expects params.value to be a single or array of phone number objects, each with { name, country (code), number (E.164), extension }
    cellRenderer: 'bootstrapPhoneNumberRenderer',
    autoHeight: true,
    // width: 300,  // TODO - remove?  nothing else sets this... its just initial width and can be adjusted, so maybe OK
    // suppressSizeToFit: true,  // no reason for this, especially with initial width set
  },
  company_tag_list: {
    // SPECIAL ONE JUST FOR CompanyTag stuff - values will be array of CompanyTag IDs
    // ACTUALLY DON'T NEED valueFormatter HERE -- IF YOU HAVE refData THEN GRID AUTOMATICALLY USES THAT IN valueFormatter
    // AND YOU CAN JUST USE THE VALUE GETTER FOR DEFAULT COLDEF
    // AND FOR EXCEL EXPORT, YOU CAN USE THE EXCEL CALLBACK TO FORMAT VALUES FROM refData
    // EXCEPT FOR SERVER-SIDE ROW MODEL -- NEED TO PROVIDE ALL OPTIONS IN ADVANCE
    enableRowGroup: false,
    enablePivot: false,
    filterParams: {
      // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
      buttons: ['reset'],
      closeOnApply: false,
      suppressSorting: true, // suppress built-in sorting (it will sort filter options by ID, which is not what we want... we will do sorting below)
      refreshValuesOnOpen: true,
      cellRenderer: 'companyTagSingleRenderer',
      // TODO: This should be used when we return objects {ID: {label:String, _url: URL}} instead of {ID: String} mapping
      // values: (params) => {
      //   // need to get values from refData on columnDef
      //   console.log('params in lookup filterParams.values', params);
      //   const refData = params.colDef.refData;
      //   // Convert refData to an array of { key, label } objects
      //   const entries = Object.entries(refData).map(([key, value]) => ({
      //     key,
      //     label: value,
      //   }));
      //   // Sort entries alphabetically by label (case-insensitive)
      //   const sortedEntries = sortBy(entries, (entry) => String(entry.label).toLocaleLowerCase());
      //   // Extract just the keys for the filter dropdown
      //   const vals = sortedEntries.map((entry) => entry.key);
      //   params.success(vals); // that returns the values to the filter dropdown
      // },
      values: (params) => {
        // need to get values from refData on columnDef
        console.log('params in lookup filterParams.values', params);
        //const vals = Object.keys(params.colDef.refData);  // make sure you always have refData?  sort by values, alphabetically?
        const vals = sortBy(
          Object.entries(params.colDef.refData).map(([k, v]) => ({ key: k, value: v })),
          (o) => String(o.value).toLocaleLowerCase(),
        ).map((e) => e.key);
        params.success(vals); // that returns the values to the filter dropdown
      },
    },
    valueFormatter: (params) => {
      // TODO - probably want to use cellRenderer instead
      // params.value is an array of CompanyTag IDs (not tagging IDs)
      //console.log('params in company_tag_list valueFormatter for field', params.colDef.field, params, 'isArray?', Array.isArray(params.value));
      const tagIds = Array.isArray(params.value) ? params.value : [];
      return tagIds.join(', ');
    },
  },
  spl_tag_list: {
    // SPECIAL ONE JUST FOR SPL TAGS stuff - values will be array of TagModel IDs
    // ACTUALLY DON'T NEED valueFormatter HERE -- IF YOU HAVE refData THEN GRID AUTOMATICALLY USES THAT IN valueFormatter
    // AND YOU CAN JUST USE THE VALUE GETTER FOR DEFAULT COLDEF
    // AND FOR EXCEL EXPORT, YOU CAN USE THE EXCEL CALLBACK TO FORMAT VALUES FROM refData
    // EXCEPT FOR SERVER-SIDE ROW MODEL -- NEED TO PROVIDE ALL OPTIONS IN ADVANCE
    enableRowGroup: false,
    enablePivot: false,
    filterParams: {
      // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
      buttons: ['reset'],
      closeOnApply: false,
      suppressSorting: true, // suppress built-in sorting (it will sort filter options by ID, which is not what we want... we will do sorting below)
      refreshValuesOnOpen: true,
      cellRenderer: 'splTagSingleRenderer',
      // TODO: This should be used when we return objects {ID: {label:String, _url: URL}} instead of {ID: String} mapping
      // values: (params) => {
      //   // need to get values from refData on columnDef
      //   console.log('params in lookup filterParams.values', params);
      //   const refData = params.colDef.refData;
      //   // Convert refData to an array of { key, label } objects
      //   const entries = Object.entries(refData).map(([key, value]) => ({
      //     key,
      //     label: value,
      //   }));
      //   // Sort entries alphabetically by label (case-insensitive)
      //   const sortedEntries = sortBy(entries, (entry) => String(entry.label).toLocaleLowerCase());
      //   // Extract just the keys for the filter dropdown
      //   const vals = sortedEntries.map((entry) => entry.key);
      //   params.success(vals); // that returns the values to the filter dropdown
      // },
      values: (params) => {
        // need to get values from refData on columnDef
        console.log('params in lookup filterParams.values', params);
        //const vals = Object.keys(params.colDef.refData);  // make sure you always have refData?  sort by values, alphabetically?
        const vals = sortBy(
          Object.entries(params.colDef.refData).map(([k, v]) => ({ key: k, value: v })),
          (o) => String(o.value).toLocaleLowerCase(),
        ).map((e) => e.key);
        params.success(vals); // that returns the values to the filter dropdown
      },
    },
    valueFormatter: (params) => {
      // TODO - probably want to use cellRenderer instead
      // params.value is an array of CompanyTag IDs (not tagging IDs)
      //console.log('params in company_tag_list valueFormatter for field', params.colDef.field, params, 'isArray?', Array.isArray(params.value));
      const tagIds = Array.isArray(params.value) ? params.value : [];
      return tagIds.join(', ');
    },
  },
  company_site_type: {
    enableRowGroup: false,
    enablePivot: false,
    filterParams: {
      // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
      buttons: ['reset'],
      closeOnApply: false,
      suppressSorting: true, // suppress built-in sorting (it will sort filter options by ID, which is not what we want... we will do sorting below)
      refreshValuesOnOpen: true,
      cellRenderer: 'companySiteTypeRenderer',
      // TODO: This should be used when we return objects {ID: {label:String, _url: URL}} instead of {ID: String} mapping
      // values: (params) => {
      //   // need to get values from refData on columnDef
      //   console.log('params in lookup filterParams.values', params);
      //   const refData = params.colDef.refData;
      //   // Convert refData to an array of { key, label } objects
      //   const entries = Object.entries(refData).map(([key, value]) => ({
      //     key,
      //     label: value,
      //   }));
      //   // Sort entries alphabetically by label (case-insensitive)
      //   const sortedEntries = sortBy(entries, (entry) => String(entry.label).toLocaleLowerCase());
      //   // Extract just the keys for the filter dropdown
      //   const vals = sortedEntries.map((entry) => entry.key);
      //   params.success(vals); // that returns the values to the filter dropdown
      // },
      values: (params) => {
        // need to get values from refData on columnDef
        console.log('params in lookup filterParams.values', params);
        //const vals = Object.keys(params.colDef.refData);  // make sure you always have refData?  sort by values, alphabetically?
        const vals = sortBy(
          Object.entries(params.colDef.refData).map(([k, v]) => ({ key: k, value: v })),
          (o) => String(o.value).toLocaleLowerCase(),
        ).map((e) => e.key);
        params.success(vals); // that returns the values to the filter dropdown
      },
    },
    valueFormatter: (params) => {
      // TODO - probably want to use cellRenderer instead
      // params.value is an array of CompanySiteType IDs
      //console.log('params in company_tag_list valueFormatter for field', params.colDef.field, params, 'isArray?', Array.isArray(params.value));
      const siteTypeIds = Array.isArray(params.value) ? params.value : [];
      return siteTypeIds.join(', ');
    },
  },
  rfx_list: {
    enableRowGroup: false,
    enablePivot: false,
    filter: false,
    valueFormatter: (params) => formatRfxListValue(params.value),
    cellRenderer: 'rfxListRenderer',
    autoHeight: true,
  },
  location: {
    // backend provides a nested object with a bunch of stuff... see that
    // make a custom renderer for this, showing country flag, maybe hover to show full address, etc.
    // currently in value: { city_state, city_state_country, country_code, country_name }
    valueFormatter: (params) => {
      // this is not ideal.... gets pretty long
      const locationParts = params.value ? [params.value.city_state, params.value.country_code] : [];
      return locationParts.filter(Boolean).join(', ');
    },
    cellRenderer: (params) => {
      // just a simple function that returns HTML for now
      // params has lots of stuff, including { value, valueFormatted, getValue(), setValue(), formatValue(), data, node, colDef, context...}
      const countryName = params.value?.country_name || '';
      // it always shows 'United States of America' for US, so let's shorten that a bit... make this more scalable later
      const countryNameForDisplay = countryName === 'United States of America' ? 'USA' : countryName;
      // you can add font-size-80 to one or both of these... looks good... but not needed with shorter country names
      return `<div class="d-flex flex-column align-items-center justify-content-center text-center h-100 gap-1" style="line-height: 1;">
                    <div class="">${params.value?.city_state || ''}</div>
                    <div class="">${countryNameForDisplay}</div>
                    </div>`;
    },
  },
  contact_full_name: {
    // this is a special one for Contact Grid, the left-most column
    cellRenderer: 'contactFullNameRenderer',
    autoHeight: true,
    pinned: 'left', // pin to left side for easier access to link in wide grids
    lockPinned: true,  // prevent unpinning
    lockPosition: true, // always show first
  },
};
